import { useEffect, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../firebase";
import type { AppGrant, LicenseRequest } from "@acruxsoft/platform-shared";
import { useAuthCtx } from "../lib/auth-provider";

/**
 * The signed-in user's licenses and license requests, live. Both collections
 * let a user read their own documents (see firestore.rules), and the `uid`
 * filter is what makes the query pass those rules.
 *
 * Live rather than a one-off read so a license Frank grants shows up on /home
 * without a reload.
 */
export function useMyLicenses() {
  const { user } = useAuthCtx();
  const [grants, setGrants] = useState<AppGrant[]>([]);
  const [requests, setRequests] = useState<LicenseRequest[]>([]);
  const [grantsReady, setGrantsReady] = useState(false);
  const [requestsReady, setRequestsReady] = useState(false);

  useEffect(() => {
    setGrants([]);
    setRequests([]);
    setGrantsReady(false);
    setRequestsReady(false);
    if (!user) {
      setGrantsReady(true);
      setRequestsReady(true);
      return;
    }
    // A failed listener must not leave the page spinning forever: treat it as
    // "nothing on record", which shows locked tiles — the safe direction.
    const stopGrants = onSnapshot(
      query(collection(db, "appGrants"), where("uid", "==", user.uid)),
      (snap) => {
        setGrants(snap.docs.map((d) => d.data() as AppGrant));
        setGrantsReady(true);
      },
      (e) => {
        console.error("[use-my-licenses] appGrants listener failed", e);
        setGrantsReady(true);
      }
    );
    const stopRequests = onSnapshot(
      query(collection(db, "licenseRequests"), where("uid", "==", user.uid)),
      (snap) => {
        setRequests(snap.docs.map((d) => d.data() as LicenseRequest));
        setRequestsReady(true);
      },
      (e) => {
        console.error("[use-my-licenses] licenseRequests listener failed", e);
        setRequestsReady(true);
      }
    );
    return () => {
      stopGrants();
      stopRequests();
    };
  }, [user]);

  return {
    grants,
    requests,
    loading: !grantsReady || !requestsReady,
    grantFor: (appId: string) => grants.find((g) => g.appId === appId) ?? null,
    requestFor: (appId: string) => requests.find((r) => r.appId === appId) ?? null
  };
}
